<template>
    <grid-area
        area="left"
        scroll
    >
        <flex-container class="flex-container--size-padding-md">
            <flex-container>
                <flex-container :size="6">
                    <contact-card-tabs-skeleton>
                        <contact-card-tabs
                            :contact-id="lead.primaryContact.id"
                            :editable="leadIsEditable"
                        />
                    </contact-card-tabs-skeleton>
                </flex-container>

                <flex-container :size="4" style="padding-top: 40px;">
                    <order-state-card-skeleton>
                        <order-state-card
                            :order="order"
                            :assignable-statusses="availableOrderStatusses"
                            :editable="orderIsEditable"
                        />
                    </order-state-card-skeleton>
                </flex-container>
            </flex-container>

            <flex-container>
                <flex-container :size="5">
                    <user-team-card-skeleton>
                        <order-referrer-card :lead="lead" :order="order" />
                    </user-team-card-skeleton>
                </flex-container>

                <flex-container :size="5">
                    <user-team-card-skeleton>
                        <order-assignee-card :lead="lead" :order="order" />
                    </user-team-card-skeleton>
                </flex-container>
            </flex-container>

            <order-financial-card-skeleton>
                <order-financial-card :editable="orderIsEditable" :order="order as Order" />
            </order-financial-card-skeleton>
        </flex-container>
    </grid-area>
</template>

<script setup lang="ts">
    import type { Lead } from '@keyvue/one-api-client';
    import useClient from '@composables/useClient';
    import { Order, OrderStatus } from '@keyvue/one-api-client-legacy';
    import { FlexContainer, GridArea } from '@keyvue/one-ui';
    import ContactCardTabsSkeleton from '@components/Skeleton/Cards/ContactCardTabsSkeleton.vue';
    import OrderStateCardSkeleton from '@components/Skeleton/Cards/Order/OrderStateCardSkeleton.vue';
    import ContactCardTabs from '@components/Cards/ContactCardTabs.vue';
    import UserTeamCardSkeleton from '@components/Skeleton/Cards/UserTeamCardSkeleton.vue';
    import { onMounted, computed, watch, toRef } from 'vue';
    import OrderAssigneeCard from '@components/Cards/Order/OrderAssigneeCard.vue';
    import OrderReferrerCard from '@components/Cards/Order/OrderReferrerCard.vue';
    import OrderStateCard from '@components/Cards/Order/OrderStateCard.vue';
    import OrderFinancialCard from '@components/Cards/Order/OrderFinancialCard.vue';
    import OrderFinancialCardSkeleton from '@components/Skeleton/Cards/Order/OrderFinancialCardSkeleton.vue';

    export interface Props {
        order: Order
        lead: Lead
    }

    const props = defineProps<Props>();
    const localOrder = toRef(props, 'order');

    const { currentCompany } = useClient();

    const leadIsEditable = computed(() => props.lead.assignee.company.equals(currentCompany.value));

    const orderIsEditable = computed(() => {
        if (!localOrder.value.assignedToBranch) {
            return false;
        }

        if (!localOrder.value.assignedToBranch.company) {
            return false;
        }

        return localOrder.value.assignedToBranch.company.id === currentCompany.value?.id.value;
    });

    const availableOrderStatusses = [
        OrderStatus.PENDING,
        OrderStatus.UNSUCCESSFUL,
        OrderStatus.SUCCESSFUL,
    ];

    onMounted(() => {
        if (!localOrder.value.assignedToBranch) {
            throw new Error('Order has no assigned branch');
        }
    });

    watch(props, () => {
        localOrder.value = props.order;
    });
</script>
